// Render an assistant message's markdown into DOMQL nodes via the symbolize
// markdown transpiler — the same transpiler workspace/chat uses.
//
// Static import: resolves cleanly in BOTH real build paths — the parcel dev
// server that serves :1235 and the chrome-connect esbuild panel bundle (which
// bundles symbolize-markdown's prebuilt dist). (Parcel's *production*
// `parcel build` has an unrelated, pre-existing scope-hoisting defect, but
// that's not how simone ships — it runs via the dev server + the esbuild
// panel, neither of which scope-hoists.)
//
// el.set() is the canonical DOMQL escape hatch for swapping a subtree
// (workspace/chat's MarkdownPreview does the same). _lastMd caches the source
// so streaming re-renders that don't change the text are skipped.
import { transformMarkdown } from '@symbo.ls/symbolize-markdown'

// Collapse substantial fenced-code blocks behind a native <details> toggle —
// "stop prompting weird codes, make them toggleable" (Nika, 2026-08-08). An
// assistant bubble narrating a task should read as prose; the code is evidence
// the user can open, not the message. Short snippets (≤3 lines and ≤160
// chars) stay inline — someone who asked for a one-liner shouldn't have to
// click to see it.
//
// Operates on the TRANSPILED tree, not the markdown: a fenced block arrives as
// `{ Pre: { tag:'pre', 'data-language', CodeBlock: { text } } }`, so the whole
// styled node nests inside the toggle unchanged — same highlighting, same
// chrome, now closed by default. <details>/<summary> on purpose: native
// disclosure needs no state key, no JS, and keyboards/screen readers get it
// for free — and this file ships into hosts (chrome-connect panel) where
// bespoke toggle state would need wiring that plain HTML already has.
export const collapseCodeFences = (tree) => {
  if (!tree || typeof tree !== 'object' || Array.isArray(tree)) return tree
  const out = {}
  for (const key of Object.keys(tree)) {
    const node = tree[key]
    const isPre = node && typeof node === 'object' && (node.tag === 'pre' || node.extends === 'Pre')
    if (!isPre) {
      out[key] = node
      continue
    }
    const codeText = (node.CodeBlock && node.CodeBlock.text) || ''
    const lines = String(codeText).split('\n').length
    if (lines <= 3 && codeText.length <= 160) {
      out[key] = node
      continue
    }
    const lang = String(node['data-language'] || '').trim()
    out['CodeToggle_' + key] = {
      tag: 'details',
      width: '100%',
      minWidth: '0',
      round: 'radiusControl',
      overflow: 'hidden',
      margin: 'Y 0',
      Summary: {
        tag: 'summary',
        padding: 'Y Z',
        fontSize: 'Z2',
        fontFamily: 'monospace',
        color: 'caption',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'B defaultBezier color',
        ':hover': { color: 'title' },
        text: (el) =>
          `${lang || 'code'} · ` +
          el
            .call('polyglot', lines === 1 ? 'ai.chat.codeLine' : 'ai.chat.codeLines')
            .replace('{count}', lines)
      },
      [key]: node
    }
  }
  return out
}

export const ChatMarkdown = {
  // FA204 / frank trap — COMPONENTS/render (+ a duplicated collapseCodeFences)
  // used to be bare module-scope const/function read from inside onCreate/
  // onUpdate (serialized factories); they read as undefined once this
  // component is frank.toJSON'd for platform delivery. Moved into `scope`,
  // called via `el.scope.<name>`. `collapseCodeFences` ALSO stays exported at
  // module scope (dual-mode, same idiom as AppOrgSwitcher's
  // `__orgSwitcherInternals`) because ChatMarkdown.test.js imports it bare for
  // offline Node coverage — the scope copy is what the live tree actually
  // calls.
  scope: {
    // Chat-bubble code mapping — same convention as renderAssistantMarkdown
    // and MarkdownPreview. Never leave these on the transpiler defaults:
    // default fenced/inline code maps to the full CodePreviewWithTitle chrome
    // (title + copy + framework switcher), which is wrong inside an
    // assistant bubble — this was the "AI-generated code renders the Code
    // preview" bug.
    COMPONENTS: {
      code: 'InlineCode',
      codeBlock: 'CodeBlock'
    },

    collapseCodeFences(tree) {
      if (!tree || typeof tree !== 'object' || Array.isArray(tree)) return tree
      const out = {}
      for (const key of Object.keys(tree)) {
        const node = tree[key]
        const isPre =
          node && typeof node === 'object' && (node.tag === 'pre' || node.extends === 'Pre')
        if (!isPre) {
          out[key] = node
          continue
        }
        const codeText = (node.CodeBlock && node.CodeBlock.text) || ''
        const lines = String(codeText).split('\n').length
        if (lines <= 3 && codeText.length <= 160) {
          out[key] = node
          continue
        }
        const lang = String(node['data-language'] || '').trim()
        out['CodeToggle_' + key] = {
          tag: 'details',
          width: '100%',
          minWidth: '0',
          round: 'radiusControl',
          overflow: 'hidden',
          margin: 'Y 0',
          Summary: {
            tag: 'summary',
            padding: 'Y Z',
            fontSize: 'Z2',
            fontFamily: 'monospace',
            color: 'caption',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'B defaultBezier color',
            ':hover': { color: 'title' },
            text: (el) =>
              `${lang || 'code'} · ` +
              el
                .call('polyglot', lines === 1 ? 'ai.chat.codeLine' : 'ai.chat.codeLines')
                .replace('{count}', lines)
          },
          [key]: node
        }
      }
      return out
    },

    render(el, s) {
      // Read the markdown source from state — s.text is the canonical
      // message string. el.text can hold the unresolved prop factory, which
      // would feed a function (not a string) into transformMarkdown.
      const body =
        s && typeof s.text === 'string' ? s.text : typeof el.text === 'string' ? el.text : ''
      if (el._lastMd === body) return
      el._lastMd = body
      if (!body) {
        el.set({ tag: 'span', text: '' })
        return
      }
      try {
        el.set(this.collapseCodeFences(transformMarkdown(body, { components: this.COMPONENTS })))
      } catch (_) {
        el.set({ tag: 'span', text: body })
      }
    }
  },

  tag: 'div',
  flow: 'y',
  gap: 'Z',
  width: '100%',
  minWidth: '0',
  onCreate: (el, s) => el.scope.render(el, s),
  onUpdate: (el, s) => el.scope.render(el, s)
}
